// src/components/WaferMapCanvas.js

import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";

const zoomLevels = [10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 125, 150, 175, 200, 250, 300, 350, 400];

export default function WaferMapCanvas({ mapInfo }) {
  const { data, yld } = mapInfo;

  const canvasRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(12);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedSbins, setSelectedSbins] = useState(new Set());
  const [renderedSbins, setRenderedSbins] = useState(new Set());

  const zoom = useMemo(() => zoomLevels[zoomLevel - 1], [zoomLevel]);
  const baseCellSize = 25;
  const cellSize = (baseCellSize * zoom) / 100;

  const { minX, maxX, minY, maxY, sbinCount, allSbinKeys } = useMemo(() => {
    if (!data || data.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0, sbinCount: {}, allSbinKeys: [] };
    }
    const sCount = {};
    data.forEach(d => {
      sCount[d.SBIN] = (sCount[d.SBIN] || 0) + 1;
    });
    const sKeys = Object.keys(sCount).map(Number).sort((a,b) => a-b);

    return {
      minX: Math.min(...data.map(d => d.X)),
      maxX: Math.max(...data.map(d => d.X)),
      minY: Math.min(...data.map(d => d.Y)),
      maxY: Math.max(...data.map(d => d.Y)),
      sbinCount: sCount,
      allSbinKeys: sKeys,
    };
  }, [data]);
  
  useEffect(() => {
    const allSbinsSet = new Set(allSbinKeys);
    setSelectedSbins(allSbinsSet);
    setRenderedSbins(allSbinsSet);
  }, [allSbinKeys]);

  const labelSpaceX = 30;
  const labelSpaceY = 40; 
  const canvasWidth = (maxX - minX + 1) * cellSize + labelSpaceX;
  const canvasHeight = (maxY - minY + 1) * cellSize + labelSpaceY;

  const sbinColors = useMemo(() => {
    const colors = {};
    colors[1] = "#4caf50";
    for (let i = 2; i <= 1001; i++) {
      colors[i] = `hsl(${(i * 30) % 360}, 70%, 60%)`;
    }
    return colors;
  }, []);

  const defaultColor = "#cccccc";
  const fadedColor = "#e0e0e0";

  const dataMap = useMemo(() => {
    if (!data) return {};
    const map = {};
    data.forEach(d => {
      map[`${d.X},${d.Y}`] = d;
    });
    return map;
  }, [data]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const ctx = canvas.getContext("2d");

    // [수정] canvas.width/height 대신 React 상태 변수를 직접 사용하여 ESLint 경고 해결
    ctx.clearRect(0, 0, canvasWidth, canvasHeight); 
    
    ctx.font = "bold 14px sans-serif";
    ctx.fillStyle = "#000";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const yldText = (typeof yld === 'number') ? `(YLD: ${yld.toFixed(2)}%)` : "";
    ctx.fillText(`Wafer Map ${yldText}`, canvasWidth / 2, 5);

    ctx.font = `${Math.max(cellSize / 2.5, 8)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const point = dataMap[`${x},${y}`];
        const px = labelSpaceX + (x - minX) * cellSize;
        const py = labelSpaceY + (y - minY) * cellSize;
        
        if (point) {
          if (renderedSbins.has(point.SBIN)) {
            ctx.fillStyle = sbinColors[point.SBIN] || defaultColor;
          } else {
            ctx.fillStyle = fadedColor;
          }
          ctx.fillRect(px, py, cellSize, cellSize);

          if (cellSize > 10 && renderedSbins.has(point.SBIN)) {
            ctx.fillStyle = "#000";
            ctx.fillText(point.SBIN, px + cellSize / 2, py + cellSize / 2);
          }
        } else {
          ctx.fillStyle = "#fff";
          ctx.fillRect(px, py, cellSize, cellSize);
        }
        ctx.strokeStyle = "#999";
        ctx.strokeRect(px, py, cellSize, cellSize);
      }
    }
    
    if (selectedPoint) {
      const x = labelSpaceX + (selectedPoint.X - minX) * cellSize;
      const y = labelSpaceY + (selectedPoint.Y - minY) * cellSize;
      ctx.strokeStyle = "#ff5722";
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, cellSize, cellSize);
      ctx.lineWidth = 1;
    }

    ctx.fillStyle = "#000";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    for (let x = minX; x <= maxX; x++) {
      const posX = labelSpaceX + (x - minX) * cellSize + cellSize / 2;
      ctx.fillText(x, posX, labelSpaceY - 2);
    }

    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let y = minY; y <= maxY; y++) {
      const posY = labelSpaceY + (y - minY) * cellSize + cellSize / 2;
      ctx.fillText(y, labelSpaceX - 5, posY);
    }
  }, [data, dataMap, yld, cellSize, minX, minY, maxX, maxY, selectedPoint, sbinColors, defaultColor, canvasWidth, canvasHeight, renderedSbins, fadedColor, labelSpaceX, labelSpaceY]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);
  
  const handleMouseMove = useCallback((e) => {
    if(!data) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const col = Math.floor((mouseX - labelSpaceX) / cellSize) + minX;
    const row = Math.floor((mouseY - labelSpaceY) / cellSize) + minY;
    const point = data.find(d => d.X === col && d.Y === row);
    setHoverPoint(point || null);
  }, [cellSize, minX, minY, data, labelSpaceX, labelSpaceY]);

  const handleClick = useCallback(() => {
    if (hoverPoint) {
      setSelectedPoint(
        selectedPoint && selectedPoint.X === hoverPoint.X && selectedPoint.Y === hoverPoint.Y
          ? null
          : hoverPoint
      );
    }
  }, [hoverPoint, selectedPoint]);

  const handleExportSVG = useCallback(() => {
    if (!data || data.length === 0) {
      alert("There is no map data to save.");
      return;
    }
    
    let svgElements = [];
    const fontSize = Math.max(cellSize / 2.5, 8);
    
    const yldText = (typeof yld === 'number') ? `(YLD: ${yld.toFixed(2)}%)` : "";
    svgElements.push(`<text x="${canvasWidth / 2}" y="5" font-family="sans-serif" font-size="14px" font-weight="bold" fill="#000" text-anchor="middle" dominant-baseline="hanging">Wafer Map ${yldText}</text>`);

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const point = dataMap[`${x},${y}`];
        const px = labelSpaceX + (x - minX) * cellSize;
        const py = labelSpaceY + (y - minY) * cellSize;
        const fill = point ? (sbinColors[point.SBIN] || defaultColor) : "#fff";
        svgElements.push(`<rect x="${px}" y="${py}" width="${cellSize}" height="${cellSize}" fill="${fill}" stroke="#999" stroke-width="1" />`);
        if (point && cellSize > 10) {
          svgElements.push(`<text x="${px + cellSize / 2}" y="${py + cellSize / 2}" font-family="sans-serif" font-size="${fontSize}px" fill="#000" text-anchor="middle" dominant-baseline="middle">${point.SBIN}</text>`);
        }
      }
    }
    
    if (selectedPoint) {
      const x = labelSpaceX + (selectedPoint.X - minX) * cellSize;
      const y = labelSpaceY + (selectedPoint.Y - minY) * cellSize;
      svgElements.push(`<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="none" stroke="#ff5722" stroke-width="3" />`);
    }

    for (let x = minX; x <= maxX; x++) {
      const posX = labelSpaceX + (x - minX) * cellSize + cellSize / 2;
      svgElements.push(`<text x="${posX}" y="${labelSpaceY - 2}" font-family="sans-serif" font-size="10px" fill="#000" text-anchor="middle" dominant-baseline="auto">${x}</text>`);
    }
    for (let y = minY; y <= maxY; y++) {
      const posY = labelSpaceY + (y - minY) * cellSize + cellSize / 2;
      svgElements.push(`<text x="${labelSpaceX - 5}" y="${posY}" font-family="sans-serif" font-size="10px" fill="#000" text-anchor="end" dominant-baseline="middle">${y}</text>`);
    }

    const svgString = `<svg width="${canvasWidth}" height="${canvasHeight}" xmlns="http://www.w3.org/2000/svg">${svgElements.join('')}</svg>`;
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "wafer-map.svg");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [data, yld, canvasWidth, canvasHeight, cellSize, dataMap, minX, minY, maxX, maxY, selectedPoint, sbinColors, defaultColor, labelSpaceX, labelSpaceY]);

  const handleSbinCheckboxChange = (sbin) => {
    setSelectedSbins(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sbin)) {
        newSet.delete(sbin);
      } else {
        newSet.add(sbin);
      }
      return newSet;
    });
  };

  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      setSelectedSbins(new Set(allSbinKeys));
    } else {
      setSelectedSbins(new Set());
    }
  };

  const handleDrawClick = () => {
    setRenderedSbins(new Set(selectedSbins));
  };

  const isAllSelected = selectedSbins.size === allSbinKeys.length && allSbinKeys.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: "sans-serif" }}>
      <div style={{ marginBottom: 10, padding: "8px", border: "1px solid #ccc", backgroundColor: "#fafafa", borderRadius: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingBottom: '8px', borderBottom: '1px solid #ddd', marginBottom: '8px'}}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="checkbox"
              id="select-all-sbins"
              checked={isAllSelected}
              onChange={handleSelectAllChange}
              disabled={allSbinKeys.length === 0}
            />
            <label htmlFor="select-all-sbins" style={{ fontWeight: 'bold', cursor: 'pointer' }}>
              Select All
            </label>
          </div>
          <button
            onClick={handleDrawClick}
            style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #007bff', backgroundColor: '#007bff', color: 'white', borderRadius: '3px', cursor: 'pointer' }}
          >
            Draw
          </button>
        </div>
        <div style={{ maxHeight: 120, overflowY: "auto", display: "flex", flexWrap: "wrap" }}>
          {allSbinKeys.map((sbin) => (
            <div key={sbin} style={{ display: "flex", alignItems: "center", margin: "4px 10px", width: '130px' }}>
              <input
                type="checkbox"
                id={`sbin-check-${sbin}`}
                checked={selectedSbins.has(sbin)}
                onChange={() => handleSbinCheckboxChange(sbin)}
                style={{ marginRight: '5px', cursor: 'pointer' }}
              />
              <label htmlFor={`sbin-check-${sbin}`} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '12px' }}>
                <div style={{ width: 15, height: 15, backgroundColor: sbinColors[sbin] || defaultColor, border: "1px solid #999", marginRight: 5, borderRadius: '2px' }} />
                <span>SBIN {sbin}: {sbinCount[sbin]}</span>
              </label>
            </div>
          ))}
        </div>
      </div>
      
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 5px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px' }}>
          <span>Zoom:</span>
          <button onClick={() => setZoomLevel(prev => Math.max(1, prev - 1))} style={{ padding: '2px 6px' }}>-</button>
          <input
            type="range"
            min="1"
            max={zoomLevels.length}
            value={zoomLevel}
            onChange={(e) => setZoomLevel(Number(e.target.value))}
            style={{ width: '120px', cursor: 'pointer' }}
          />
          <button onClick={() => setZoomLevel(prev => Math.min(zoomLevels.length, prev + 1))} style={{ padding: '2px 5px' }}>+</button>
          <span style={{ minWidth: '100px', textAlign: 'left' }}>
            Level {zoomLevel} ({zoom}%)
          </span>
        </div>
        <button onClick={handleExportSVG} style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #ccc', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>
          Save as SVG
        </button>
      </div>

      <div style={{ margin: "10px 0", height: '20px', fontSize: '13px' }}>
        {selectedPoint ? (
          <div><strong>선택:</strong> X={selectedPoint.X}, Y={selectedPoint.Y}, SBIN={selectedPoint.SBIN}</div>
        ) : hoverPoint ? (
          <div><strong>마우스:</strong> X={hoverPoint.X}, Y={hoverPoint.Y}, SBIN={hoverPoint.SBIN}</div>
        ) : (
          <div style={{ color: '#777' }}>Hover the mouse to view the information.</div>
        )}
      </div>

      <div style={{ border: "1px solid #ddd", overflow: "auto", flex: 1, borderRadius: '4px' }}>
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
          onMouseLeave={() => setHoverPoint(null)}
          style={{ display: "block", cursor: "crosshair" }}
        />
      </div>
    </div>
  );
}